<template>
  <div :class="type">
    <item-header
      :id="id"
      :header_text="'Spa package'"
      :confirmed="confirmed"
      :locked="locked"
      :tbc_ok="tbc_ok"
      :deletion_confirmed="deletion_confirmed"
      :confirmation_reference="confirmation_reference"
      :rejection_reason="rejection_reason"
      :update-attribute="updateAttribute"
      :deleted_at="deleted_at"
      :errors="errors"
      :is-ygt-booking="isYgtBooking"
      :confirmation_reference_required="confirmation_reference_required"
    />

    <section class="section markdown">
      <h3>{{ title }}</h3>
      <div v-html="descriptionHTML" />
    </section>

    <section
      v-if="supplier_notes !== ''"
      class="section supplier-notes markdown"
    >
      <h4>Notes to suppliers</h4>
      <div v-html="supplierNotesHTML" />
    </section>

    <section
      v-if="medical_notes !== ''"
      class="section medical-notes"
    >
      <h4>Medical notes</h4>
      <p
        v-for="line in medicalNotesLines"
        :key="line"
      >
        {{ line }}
      </p>
    </section>

    <section
      v-if="sbGuestLines.length > 0 && !isYgtBooking"
      class="guests section"
    >
      <h4>Guests</h4>
      <ul>
        <li
          v-for="line in sbGuestLines"
          :key="line"
        >
          {{ line }}
        </li>
      </ul>
    </section>

    <section class="section">
      <ol
        v-if="lunchTimeTexts.length > 0 && isYgtBooking"
        class="detail list"
      >
        <li
          v-for="lunchTimeText in lunchTimeTexts"
          :key="lunchTimeText"
        >
          {{ lunchTimeText }}
        </li>
      </ol>

      <section
        v-if="treatments.length > 0 && !isYgtBooking"
        class="treatments"
      >
        <h4>Guests & Treatments</h4>
        <p>Please check the details displayed below. Any information in red requires attention and updating.</p>
        <treatments-table
          :id="id"
          :min-date="booking_travel_date"
          :max-date="booking_return_date"
          :treatments="treatments"
          :treatment-errors="errors.treatments"
          :update-nested-attribute="updateNestedAttribute"
          :mark-form-fields-as-invalid="markFormFieldsAsInvalid"
        />
        <div
          v-if="errors.form_fields"
          class="input-error"
        >
          <p>Some dates or time fields are not fully filled.</p>
        </div>
      </section>

      <ol
        v-if="treatmentTexts.length > 0 && isYgtBooking"
        class="detail list"
      >
        <li
          v-for="treatmentText in treatmentTexts"
          :key="treatmentText"
        >
          {{ treatmentText }}
        </li>
      </ol>
    </section>

    <section
      v-if="lunch_times.length > 0 && !isYgtBooking"
      class="meals"
    >
      <h4>Meals</h4>
      <p>Please check the details displayed below. Any information in red requires attention and updating.</p>
      <meals-table
        :id="id"
        :min-date="booking_travel_date"
        :max-date="booking_return_date"
        :lunch-times="lunch_times"
        :meal-errors="errors.lunch_times"
        :update-nested-attribute="updateNestedAttribute"
      />
    </section>

    <section
      v-if="guestLines.length > 0 && isYgtBooking"
      class="guests"
    >
      <h4>Guests</h4>
      <p
        v-for="line in guestLines"
        :key="line"
      >
        {{ line }}
      </p>
    </section>

    <deleted-at
      :deleted_at="deleted_at"
    />
  </div>
</template>

<script>
import { inflect } from 'inflection'
import markdownit from 'markdown-it'

import { date, optionalDateTime, optionalTime } from 'lib/date-and-time-formats'

import ItemHeader from '../ItemHeader'
import DeletedAt from '../DeletedAt'
import MealsTable from '../MealsTable'
import TreatmentsTable from '../TreatmentsTable'
import commonProps from './common-props'
const markdown = markdownit({ html: true })

export default {
  components: {
    ItemHeader,
    DeletedAt,
    MealsTable,
    TreatmentsTable
  },

  props: {
    ...commonProps,

    package_description: {
      type: String,
      default: null
    },

    date: {
      type: String,
      default: null
    },

    booking_travel_date: {
      type: String,
      default: null
    },

    booking_return_date: {
      type: String,
      default: null
    },

    lunch_times: {
      type: Array,
      default: () => []
    },

    treatments: {
      type: Array,
      default: () => []
    },

    updateNestedAttribute: {
      type: Function,
      required: true
    },

    invalid: {
      type: Boolean,
      default: null
    },

    markFormFieldsAsInvalid: {
      type: Function,
      required: true
    }
  },

  computed: {
    title () {
      return this.package_description.split('\n')[0]
    },

    descriptionText () {
      return this.package_description.split('\n').slice(1).join('\n')
    },

    descriptionHTML () {
      return markdown.render(this.descriptionText)
    },

    lunchTimeTexts () {
      return this.lunch_times.map(this.generateLunchTimeText)
    },

    treatmentTexts () {
      return this.treatments.map(this.generateTreatmentText)
    },

    supplierNotesHTML () {
      return markdown.render(this.supplier_notes)
    },

    medicalNotesLines () {
      if (!this.medical_notes) return ''

      return this.medical_notes.split(/\n/g)
    },

    guestLines () {
      if (!this.guests) return []

      return this.guests.map(
        guest => {
          const name = [guest.first_name, guest.surname].filter(Boolean).join(' ')
          const stock = guest.stock?.type === 'Protected' || guest.stock?.type === 'Committed'
            ? ' (' + guest.stock.type + ')'
            : ''
          return name + ': ' + guest.unit_type + stock
        }
      )
    },

    sbGuestLines () {
      if (!this.guests) return []
      return this.guests.map(
        guest => {
          const name = [guest.first_name, guest.surname].filter(Boolean).join(' ')
          const stock = guest.stock?.type === 'Protected' || guest.stock?.type === 'Committed'
            ? ' (' + guest.stock.type + ')'
            : ''

          const details = guest.unit_type + stock

          if (details === '') {
            return name
          }
          return name + ': ' + guest.unit_type + stock
        }
      ).filter(line => line !== '')
    }
  },

  methods: {
    generateLunchTimeText (lunchTime) {
      return `Lunch-time ${date(lunchTime.date)}` +
        ` at ${optionalTime(lunchTime.time)}`
    },

    generateTreatmentText (treatment) {
      const personCount = treatment.number_of_people

      return `${personCount} ${inflect('guest', personCount)}` +
        ` for ${treatment.description}` +
        ` ${optionalDateTime(treatment.date, treatment.time)}`
    }
  }
}
</script>
